import DashboardIcon from "@mui/icons-material/Dashboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleIcon from "@mui/icons-material/People";
import EngineeringIcon from "@mui/icons-material/Engineering";
import InventoryIcon from "@mui/icons-material/Inventory2";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import MathIcon from "@mui/icons-material/Functions";
import WebIcon from "@mui/icons-material/Web";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { canAccess, hasExactAccess, resolveIdentity } from "../access";

export const PRODUCTS = [
  { id: "crm", label: "Comercial" },
  { id: "ops", label: "Demo", demo: true },
  { id: "analisis", label: "Análisis", demo: true },
];

const PRODUCT_HOME = {
  crm: "/crm",
  ops: "/",
  analisis: "/analisis",
};

const STORAGE_KEY = "trazia-product";

export const NAV_SECTIONS = [
  {
    label: null,
    product: "ops",
    items: [
      {
        text: "Resumen",
        icon: DashboardIcon,
        path: "/",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
    ],
  },
  {
    label: "Administración",
    product: "ops",
    items: [
      {
        text: "Calendario",
        icon: CalendarMonthIcon,
        path: "/calendario",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
      {
        text: "Servicios",
        icon: BuildCircleIcon,
        path: "/servicios",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
      {
        text: "Clientes",
        icon: PeopleIcon,
        path: "/clientes",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
      {
        text: "Distribución",
        icon: WebIcon,
        path: "/distribucion",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
      {
        text: "Análisis",
        icon: MathIcon,
        path: "/analisis-demo",
        roles: ["admin", "rh", "scheduler", "supervisor"],
      },
    ],
  },
  {
    label: "Comercial",
    product: "crm",
    items: [
      {
        text: "CRM",
        icon: HandshakeIcon,
        path: "/crm/admin",
        roles: ["crm_manager"],
      },
      {
        text: "Mi Cartera",
        icon: HandshakeIcon,
        path: "/crm/ejecutivo",
        roles: ["crm_agent", "crm_marketer"],
        exclusive: true,
      },
    ],
  },
  {
    label: "Análisis",
    product: "analisis",
    items: [
      {
        text: "Análisis",
        icon: MathIcon,
        path: "/analisis",
        roles: ["admin", "dev"],
      },
    ],
  },
  {
    label: "Sistema",
    product: null,
    items: [
      {
        text: "Seguridad",
        icon: ShieldOutlinedIcon,
        path: "/seguridad",
        roles: ["admin"],
        adminOnly: true,
      },
    ],
  },
  {
    label: "Supervisión",
    product: "ops",
    items: [
      {
        text: "Inventario",
        icon: InventoryIcon,
        path: "/inventario",
        roles: ["admin", "rh", "supervisor"],
        inDevelopment: true,
      },
      {
        text: "Recursos Humanos",
        icon: EngineeringIcon,
        path: "/tecnicos",
        roles: ["admin", "rh", "supervisor"],
        inDevelopment: true,
      },
    ],
  },
];

function itemVisible(user, item) {
  if (item.adminOnly) {
    const account = resolveIdentity(user).account;
    if (account === "admin") return true;
    if (account === "dev") return "disabled";
    return false;
  }
  return item.exclusive ? hasExactAccess(user, item.roles) : canAccess(user, item.roles);
}

export function getAccessibleProducts(user) {
  if (!user) return [];
  const identity = resolveIdentity(user);
  if (identity.status === "suspended") return [];
  const staff = identity.account === "admin" || identity.account === "dev";
  return PRODUCTS.filter((product) => {
    if (product.id === "analisis") return staff;
    if (product.id === "ops") return staff || Boolean(identity.grants.ops);
    return product.id === "crm";
  });
}

export function productHome(productId) {
  return PRODUCT_HOME[productId] || "/";
}

export function productFromPath(pathname) {
  if (pathname === "/analisis" || pathname.startsWith("/analisis/")) return "analisis";
  if (pathname.startsWith("/crm")) return "crm";
  const opsPaths = ["/", "/calendario", "/servicios", "/clientes", "/distribucion", "/analisis-demo", "/inventario", "/tecnicos"];
  if (opsPaths.some((path) => (path === "/" ? pathname === "/" : pathname.startsWith(path)))) {
    return "ops";
  }
  return null;
}

export function readStoredProduct() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeProduct(productId) {
  try {
    localStorage.setItem(STORAGE_KEY, productId);
  } catch {
    /* ignore */
  }
}

export function resolveActiveProduct(user, pathname) {
  const accessible = getAccessibleProducts(user);
  if (!accessible.length) return null;

  const fromPath = productFromPath(pathname);
  if (fromPath && accessible.some((product) => product.id === fromPath)) return fromPath;

  const stored = readStoredProduct();
  if (stored && accessible.some((product) => product.id === stored)) return stored;

  return accessible.some((product) => product.id === "crm") ? "crm" : accessible[0].id;
}

export function getVisibleNavItems(user, productId) {
  if (!user) return [];

  return NAV_SECTIONS.flatMap((section) => {
    if (section.product && section.product !== productId) return [];
    return section.items.flatMap((item) => {
      const visible = itemVisible(user, item);
      if (!visible) return [];
      return [{ ...item, disabled: visible === "disabled" }];
    });
  });
}

export function isNavItemActive(pathname, path) {
  if (path === "/") return pathname === "/";
  if (path === "/crm/admin") return pathname.startsWith("/crm/admin");
  if (path === "/crm/ejecutivo") return pathname.startsWith("/crm/ejecutivo");
  return pathname.startsWith(path);
}
